"use client";

import * as api from "@/src/api/sdk.gen";
import { Link } from "@/src/lib/next-compat";
import { useUserProfile } from "@/src/lib/hooks/useUserProfile";
import { Loader2, PlayCircle, ArrowRight } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

type PlansPayload = {
  course: { id: number; uuid: string; title: string };
  levels: Array<{
    planId: number | null;
    levelId: number;
    levelName: string;
    levelNumber: number;
    description: string | null;
    priceFormatted: string | null;
    currency: string | null;
    isPurchased: boolean;
    isCompleted: boolean;
    isEligible: boolean;
    isLocked: boolean;
    hasAccess: boolean;
  }>;
  hasActiveSubscription: boolean;
  accessMode?: "SPONSORED" | "INDIVIDUAL";
  hasPlacementQuiz: boolean;
  placementDetermined: boolean;
  placementSuggestion: string | null;
};

export default function CoursePlansSelection({ courseUuid }: { courseUuid: string }) {
  const { data: userProfile } = useUserProfile();
  const isOrgMember = (userProfile?.organizations?.length ?? 0) > 0;
  const [data, setData] = useState<PlansPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error, response } = await api.getCoursePlansApiV1CoursesCourseIdPlansGet({
        path: { course_id: courseUuid }
      });
      const json = (data || error || {}) as PlansPayload & { error?: string; detail?: string };

      if (error) {
        setError(json.error ?? json.detail ?? `Could not load plans (${response?.status}).`);
        setData(null);
        return;
      }

      setData(json);
    } catch {
      setError("Network error. Please try again.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [courseUuid]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-red-600">{error ?? "Something went wrong."}</p>
        <button
          type="button"
          onClick={() => void load()}
          className="mt-4 text-sm font-medium text-indigo-600 hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto py-16 px-4">
      {/* Header */}
      <div className="mb-10">
        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">
          {data.course.title}
        </p>
        <h1 className="text-2xl font-bold text-gray-900">Choose a level</h1>
        <p className="mt-2 text-sm text-gray-600">
          Purchase levels separately. Higher levels unlock for purchase after you complete the previous level.
        </p>
      </div>

      {/* Options */}
      <div className="space-y-3">
        {(data.accessMode === "SPONSORED" || isOrgMember) && (
          <div className="border border-indigo-200 bg-indigo-50 rounded-xl p-4 text-sm text-indigo-900">
            Included via your organization (no checkout required).
          </div>
        )}
        {data.hasActiveSubscription && (
          <div className="border border-green-200 bg-green-50 rounded-xl p-4 text-sm text-green-800">
            You have an active subscription. Eligible levels are included (no purchase needed).
          </div>
        )}

        {data.hasPlacementQuiz && !data.placementDetermined ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50/90 p-5 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <PlayCircle className="h-5 w-5 text-amber-600 shrink-0" />
                  <p className="font-bold text-amber-900">Placement test required</p>
                </div>
                <p className="text-xs sm:text-sm text-amber-800 leading-relaxed">
                  Please complete the placement test first to unlock the available levels.
                </p>
              </div>
              <Link
                to={`/placement-quiz/${courseUuid}`}
                className="shrink-0 inline-flex items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 transition-colors"
              >
                <span>Take placement test</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        ) : data.placementSuggestion ? (
          <div className="rounded-xl border border-indigo-100 bg-indigo-50/70 p-4 shadow-sm flex items-center justify-between gap-4 text-xs sm:text-sm text-indigo-900">
            <div>
              <span className="font-semibold">Recommended Level: </span>
              <span className="font-bold text-indigo-700">{data.placementSuggestion}</span>
            </div>
            <Link
              to={`/placement-quiz/${courseUuid}`}
              className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 hover:underline shrink-0"
            >
              Retake assessment →
            </Link>
          </div>
        ) : null}

        {data.levels.map((lvl) => {
          const isSuggested =
            data.placementSuggestion &&
            lvl.levelName.toLowerCase() === data.placementSuggestion.toLowerCase();

          const isSponsored = data.accessMode === "SPONSORED" || isOrgMember;

          const canCheckout =
            !data.hasActiveSubscription &&
            !isSponsored &&
            lvl.planId != null &&
            !lvl.isPurchased &&
            lvl.isEligible &&
            !lvl.isLocked;

          const disabled =
            lvl.planId == null ||
            lvl.isLocked ||
            !lvl.isEligible ||
            lvl.isPurchased ||
            data.hasActiveSubscription ||
            isSponsored;

          return (
            <div
              key={lvl.levelId}
              className={[
                "border rounded-xl p-5 bg-white",
                isSuggested ? "border-indigo-300 ring-1 ring-indigo-200" : "border-gray-200",
              ].join(" ")}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-900">{lvl.levelName}</p>
                    {isSuggested && (
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-indigo-600 text-white">
                        Suggested
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">Level {lvl.levelNumber}</p>
                </div>
                <p className="text-lg font-bold text-gray-900 whitespace-nowrap">
                  {isSponsored ? "Included" : (lvl.priceFormatted ?? "—")}
                </p>
              </div>

              <div className="mt-4 flex items-center justify-between gap-3">
                {lvl.isPurchased ? (
                  <span className="text-xs font-semibold text-green-700">Purchased</span>
                ) : lvl.isCompleted ? (
                  <span className="text-xs font-semibold text-green-700">Completed</span>
                ) : data.hasPlacementQuiz && !data.placementDetermined ? (
                  <span className="text-xs font-semibold text-amber-700">Placement required</span>
                ) : lvl.isLocked || !lvl.isEligible ? (
                  <span className="text-xs font-semibold text-gray-500">Locked</span>
                ) : (
                  <span className="text-xs font-semibold text-gray-500">Available</span>
                )}

                {canCheckout ? (
                  <Link
                    to={`/checkout/${lvl.planId}`}
                    className="px-4 py-2 text-center rounded-lg bg-indigo-600 text-sm font-semibold text-white hover:bg-indigo-700 transition"
                  >
                    Continue to checkout
                  </Link>
                ) : (
                  <button
                    type="button"
                    disabled={disabled}
                    className="px-4 py-2 rounded-lg bg-gray-100 text-sm font-semibold text-gray-400 cursor-not-allowed"
                  >
                    {isSponsored
                      ? "Included"
                      : data.hasActiveSubscription
                        ? "Included"
                        : lvl.isPurchased
                          ? "Purchased"
                          : lvl.planId == null
                            ? "Unavailable"
                            : data.hasPlacementQuiz && !data.placementDetermined
                              ? "Locked"
                              : lvl.isLocked || !lvl.isEligible
                                ? "Locked"
                                : "Checkout"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Back */}
      <div className="mt-8 text-center">
        <Link
          to={`/courses/${courseUuid}`}
          className="text-sm text-gray-400 hover:text-gray-700 transition"
        >
          ← Back to course
        </Link>
      </div>
    </div>
  );
}
